import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';

/**
 * Who sees each User Management surface — ONE rule for every place it can appear: the top-bar
 * teamspace menu's "Manage" section, the profile menu for single-teamspace users, and the Settings
 * side panel.
 *
 * DECISION (Kati, 2026-10-08) — EVERY ROLE SEES ALL FOUR MENUS. The gate is the PLAN
 * (`folderLevelSecurity`) and nothing else; no entry here reads a role or a `*.view` permission any
 * more. What a role may DO is decided per page and enforced by the backend — never by hiding the
 * menu.
 *
 * This reverses the role half of #2351, deliberately. That issue hid Users from anyone below Admin
 * without `members.view`, on the reasoning that it is an ACCOUNT-WIDE list. It is not account-wide
 * for them: `MembersPage` enters directory mode only under `isAccountScope`
 * (`isRootOwner || isRootSuperAdmin`), so every other role already got a single teamspace's roster —
 * no Team column, no cross-teamspace filters, and no `accountUserDirectory` query, which is
 * OWNER/SUPER_ADMIN-only server-side regardless. The page was already scoped; the menu gate was
 * withholding a view that would have been safe to show.
 *
 * So `members.view` is CAPPED: it is no longer the key to an account-wide page, because nothing here
 * opens one. The most it can reach is the teamspace-scoped list every role now reaches anyway.
 *
 * Owner and Super Admin are UNCHANGED — they keep the account-wide directory. "Even root admin sees
 * only the teamspace users list" is about ADMIN, which has been teamspace-scoped all along
 * (`MembersPage.tsx` `isAccountScope`); this change does not touch that line.
 */
export function useUserManagementAccess() {
  const { folderLevelSecurity } = useCompanyFeatures();

  return {
    /**
     * Governs the `/app/s/members` route guard, the Settings "Users" row, and the Teamspaces route's
     * branch. Unconditional now — the page is safe for any role because the SCOPE lives in
     * `MembersPage` and in the backend, not in this flag.
     *
     * NOT folded into `folderLevelSecurity`: the Users page is open on EVERY plan, so gating it on
     * the plan flag would hide it from owners of accounts that have no Group Security at all. That
     * plan-independence predates this change and is preserved.
     */
    usersPage: true,
    /** The Group Security shortcut for Users, which only exists on a folderLevelSecurity account. */
    users: folderLevelSecurity,
    teamspaces: folderLevelSecurity,
    roles: folderLevelSecurity,
    groups: folderLevelSecurity,
  };
}
